import React, { useState, useMemo } from "react";
import { format, parseISO, endOfWeek, startOfMonth, endOfMonth, addWeeks, addMonths } from "date-fns";
import { nb } from "date-fns/locale";
import {
  useTimeEntries,
  useDeleteTimeEntry,
  type TimeEntry,
} from "@/hooks/use-time-registration";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatHours } from "@/lib/format";
import TimebankEntryButton from "./TimebankEntryButton";
import DayGridView from "./DayGridView";
import { SrIcon } from "@/components/ui/SrIcon";
import { usePersistedCollapse } from "@/hooks/use-persisted-collapse";
import { RegistrationEditDialog } from "./RegistrationEditDialog";
import AbsenceRangeSummary from "./AbsenceRangeSummary";

type ViewMode = "day" | "week" | "month";

interface MonthlyEntriesCardProps {
  userId: string | null;
  weekStart: Date;
}

/** Check if a date string has a real time component (not midnight) */
function hasTimeComponent(dateStr: string): boolean {
  const d = new Date(dateStr);
  return d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
}

/** Extract HH:MM from an ISO date string in local time */
function extractTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Compute end time from start + duration */
function computeEndTime(startDate: string, durationSeconds: number): string {
  const d = new Date(new Date(startDate).getTime() + durationSeconds * 1000);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function MonthlyEntriesCard({ userId, weekStart }: MonthlyEntriesCardProps) {
  // VIB-2100: husket per bruker, som de andre seksjonene på Mine oppgaver.
  const [expanded, setExpanded] = usePersistedCollapse("monthly-entries", true);
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const { mutate: deleteEntry } = useDeleteTimeEntry();

  const currentWeek = addWeeks(weekStart, weekOffset);
  const currentMonth = addMonths(weekStart, monthOffset);

  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const rangeStart = viewMode === "week" ? currentWeek : monthStart;
  const rangeEnd = viewMode === "week" ? weekEnd : monthEnd;

  const { data: entries = [] } = useTimeEntries(
    userId,
    format(rangeStart, "yyyy-MM-dd"),
    format(rangeEnd, "yyyy-MM-dd")
  );

  const editingEntry = editingEntryId
    ? entries.find((e) => e.id === editingEntryId) ?? null
    : null;

  // Group entries by day
  const entriesByDay = useMemo(() => {
    const grouped = new Map<string, TimeEntry[]>();
    entries.forEach((entry) => {
      // VIB-704: grupper på YYYY-MM-DD slik at flere føringer samme dag
      // havner under én date-heading. start_date er timestamptz, så bare
      // tar første 10 tegn (date-delen).
      const dateKey = entry.start_date.slice(0, 10);
      if (!grouped.has(dateKey)) grouped.set(dateKey, []);
      grouped.get(dateKey)!.push(entry);
    });
    // Sorter dager descending (nyeste først), entries per dag ascending på start-tid
    return Array.from(grouped.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, dayEntries]) => [
        date,
        [...dayEntries].sort((a, b) => a.start_date.localeCompare(b.start_date)),
      ] as const);
  }, [entries]);

  const totalHours = useMemo(
    () => Math.round(entries.reduce((sum, e) => sum + e.duration_seconds / 3600, 0) * 10) / 10,
    [entries]
  );

  const title = viewMode === "week"
    ? `Uke ${format(currentWeek, "w")} (${format(currentWeek, "dd.MM")} – ${format(weekEnd, "dd.MM")})`
    : format(monthStart, "MMMM yyyy", { locale: nb }).replace(/^\w/, c => c.toUpperCase());

  const handlePrev = () => viewMode === "week" ? setWeekOffset(o => o - 1) : setMonthOffset(o => o - 1);
  const handleNext = () => viewMode === "week" ? setWeekOffset(o => o + 1) : setMonthOffset(o => o + 1);

  return (
    <div className="border-[1.5px] border-foreground bg-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <SrIcon name="clock" className="w-4 h-4 text-muted-foreground"  />
          <span className="font-display text-lg tracking-wide">Timer</span>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
            {totalHours}t
          </span>
        </div>
        {expanded ? (
          <SrIcon name="chevron-up" className="w-4 h-4 text-muted-foreground"  />
        ) : (
          <SrIcon name="chevron-down" className="w-4 h-4 text-muted-foreground"  />
        )}
      </button>

      {expanded && (
        <div className="border-t">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2 px-4 py-2 border-b border-border/50">
            <div className="flex items-center justify-between xl:justify-start gap-1">
              {viewMode === "day" ? (
                <div className="flex-1 xl:flex-none xl:min-w-[180px]" />
              ) : (
                <>
                  <button
                    onClick={handlePrev}
                    className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center hover:bg-muted rounded-md transition-colors"
                    aria-label="Forrige"
                  >
                    <SrIcon name="chevron-left" className="w-4 h-4 text-muted-foreground"  />
                  </button>
                  <p className="text-xs text-muted-foreground flex-1 xl:flex-none xl:min-w-[180px] text-center">{title}</p>
                  <button
                    onClick={handleNext}
                    className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center hover:bg-muted rounded-md transition-colors"
                    aria-label="Neste"
                  >
                    <SrIcon name="chevron-right" className="w-4 h-4 text-muted-foreground"  />
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-md p-0.5 self-center xl:self-auto">
              <button
                onClick={() => setViewMode("day")}
                className={cn(
                  "px-3 py-1.5 text-xs rounded font-medium transition-colors",
                  viewMode === "day" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                Dag
              </button>
              <button
                onClick={() => setViewMode("week")}
                className={cn(
                  "px-3 py-1.5 text-xs rounded font-medium transition-colors",
                  viewMode === "week" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                Uke
              </button>
              <button
                onClick={() => setViewMode("month")}
                className={cn(
                  "px-3 py-1.5 text-xs rounded font-medium transition-colors",
                  viewMode === "month" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                Måned
              </button>
            </div>
          </div>

          {/* A4-smoke-fiks (VIB-1221): fravær i det synlige uke-/månedsvinduet,
              konsistent med Avslutt måned. Dag-visningen (DayGridView) har sitt
              eget rutenett og dekkes ikke her — egen oppfølging ved behov. */}
          {viewMode !== "day" && (
            <AbsenceRangeSummary userId={userId} rangeStart={rangeStart} rangeEnd={rangeEnd} />
          )}

          {viewMode === "day" && <DayGridView userId={userId} />}

          {/* Mobile (under md): stacked cards per dag */}
          {viewMode !== "day" && (
          <>
          <div className="md:hidden">
            {entriesByDay.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                Ingen timer registrert
              </p>
            ) : (
              <div className="divide-y divide-border/50">
                {entriesByDay.map(([dateKey, dayEntries]) => {
                  const dayTotal = dayEntries.reduce((sum, e) => sum + e.duration_seconds, 0) / 3600;
                  return (
                    <div key={dateKey}>
                      {/* Day header */}
                      <div className="flex items-center justify-between bg-muted/30 px-4 py-2">
                        <p className="text-xs font-medium capitalize">
                          {format(parseISO(dateKey), "eeee, dd.MM", { locale: nb })}
                        </p>
                        <p className="text-xs font-bold tabular-nums">{formatHours(dayTotal)}</p>
                      </div>
                      {/* Entry cards */}
                      <div className="p-3 space-y-2">
                        {dayEntries.map((entry) => {
                          const entryHours = entry.duration_seconds / 3600;
                          const hasTime = hasTimeComponent(entry.start_date);
                          const timeDisplay = hasTime
                            ? `${extractTime(entry.start_date)}–${computeEndTime(entry.start_date, entry.duration_seconds)}`
                            : "";

                          return (
                            <div
                              key={entry.id}
                              className="border rounded-md p-3 bg-background flex items-start gap-2"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium break-words">
                                  {entry.task_description}
                                </p>
                                <p className="text-xs text-muted-foreground tabular-nums mt-0.5">
                                  {timeDisplay && <>{timeDisplay} · </>}
                                  {formatHours(entryHours)}
                                </p>
                                <div className="mt-2">
                                  <TimebankEntryButton
                                    timeEntryId={entry.id}
                                    budgetLineId={entry.budget_line_id}
                                    taskDescription={entry.task_description}
                                    entryHours={entryHours}
                                  />
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setEditingEntryId(entry.id)}
                                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:bg-muted rounded shrink-0"
                                aria-label="Rediger registrering"
                              >
                                <SrIcon name="pencil" className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  deleteEntry(entry.id, {
                                    onSuccess: () => toast.success("Timer slettet"),
                                    onError: (err) => toast.error((err as Error).message),
                                  })
                                }
                                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-muted rounded shrink-0"
                                aria-label="Slett registrering"
                              >
                                <SrIcon name="trash2" className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Desktop (md+): table */}
          <div className="hidden md:block">
          {entriesByDay.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              Ingen timer registrert
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground text-xs uppercase tracking-wider border-b">
                  <th className="text-left py-2 px-4 font-medium">Oppgave</th>
                  <th className="text-right py-2 px-4 font-medium whitespace-nowrap">Timer</th>
                  <th className="py-2 px-4 w-44" />
                </tr>
              </thead>
              <tbody>
                {entriesByDay.map(([dateKey, dayEntries]) => {
                  const dayTotal = dayEntries.reduce((sum, e) => sum + e.duration_seconds, 0) / 3600;
                  return (
                    <React.Fragment key={dateKey}>
                      {/* Day header row */}
                      <tr className="bg-muted/30">
                        <td className="py-2 px-4 text-xs font-medium capitalize">
                          {format(parseISO(dateKey), "eeee, dd.MM", { locale: nb })}
                        </td>
                        <td className="py-2 px-4 text-xs font-bold tabular-nums text-right whitespace-nowrap">
                          {formatHours(dayTotal)}
                        </td>
                        <td />
                      </tr>
                      {/* Entry rows */}
                      {dayEntries.map((entry) => {
                        const entryHours = entry.duration_seconds / 3600;
                        const hasTime = hasTimeComponent(entry.start_date);
                        const timeDisplay = hasTime
                          ? `${extractTime(entry.start_date)}–${computeEndTime(entry.start_date, entry.duration_seconds)}`
                          : "";

                        return (
                          <tr key={entry.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30 group">
                            <td className="py-2.5 px-4 text-xs text-muted-foreground">
                              <span>{entry.task_description}</span>
                              {timeDisplay && (
                                <span className="ml-2 text-[10px] text-muted-foreground/60">
                                  {timeDisplay}
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-xs tabular-nums text-right whitespace-nowrap">
                              {formatHours(entryHours)}
                            </td>
                            <td className="py-2.5 px-4">
                              <div className="flex items-center gap-2 justify-end">
                                <TimebankEntryButton
                                  timeEntryId={entry.id}
                                  budgetLineId={entry.budget_line_id}
                                  taskDescription={entry.task_description}
                                  entryHours={entryHours}
                                  compact
                                />
                                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 group-focus-within:opacity-100">
                                  <button
                                    type="button"
                                    onClick={() => setEditingEntryId(entry.id)}
                                    className="p-1 hover:bg-muted rounded"
                                    aria-label="Rediger registrering"
                                  >
                                    <SrIcon name="pencil" className="w-3 h-3 text-muted-foreground"  />
                                  </button>
                                  <button
                                    onClick={() =>
                                      deleteEntry(entry.id, {
                                        onSuccess: () => toast.success("Timer slettet"),
                                        onError: (err) => toast.error((err as Error).message),
                                      })
                                    }
                                    className="p-1 hover:bg-destructive/10 rounded"
                                  >
                                    <SrIcon name="trash2" className="w-3 h-3 text-destructive"  />
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
          </div>
          </>
          )}
        </div>
      )}

      <RegistrationEditDialog
        entry={editingEntry}
        open={!!editingEntryId}
        onOpenChange={(open) => {
          if (!open) setEditingEntryId(null);
        }}
      />
    </div>
  );
}
